import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import VLOPSubscriptionCard from './components/VLOPSubscriptionCard';
import VerdictPropagationDashboard from './components/VerdictPropagationDashboard';
import PolicyActionPanel from './components/PolicyActionPanel';
import ComplianceTracker from './components/ComplianceTracker';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PlatformBridgeInterface = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for VLOP subscriptions
  const mockSubscriptions = [
    {
      id: 'vlop-1',
      platformName: 'Meta Platforms',
      endpoint: 'https://api.meta.com/dsa/verdicts',
      status: 'active',
      verdictsSent: 1247,
      successRate: 98.5,
      pendingActions: 3,
      lastSync: 2,
      contentTypes: ['text', 'image', 'video'],
      languages: ['en', 'de', 'fr', 'es'],
      priority: 'high',
      dsaCompliant: true,
      lastAudit: '2025-01-15',
      avgResponseTime: 245
    },
    {
      id: 'vlop-2',
      platformName: 'Google/YouTube',
      endpoint: 'https://api.youtube.com/dsa/content',
      status: 'active',
      verdictsSent: 892,
      successRate: 96.2,
      pendingActions: 7,
      lastSync: 1,
      contentTypes: ['video', 'text'],
      languages: ['en', 'de', 'fr'],
      priority: 'high',
      dsaCompliant: true,
      lastAudit: '2025-01-18',
      avgResponseTime: 189
    },
    {
      id: 'vlop-3',
      platformName: 'TikTok',
      endpoint: 'https://api.tiktok.com/dsa/moderation',
      status: 'pending',
      verdictsSent: 456,
      successRate: 89.3,
      pendingActions: 12,
      lastSync: 6,
      contentTypes: ['video', 'audio'],
      languages: ['en', 'de'],
      priority: 'medium',
      dsaCompliant: false,
      lastAudit: '2025-01-10',
      avgResponseTime: 432
    },
    {
      id: 'vlop-4',
      platformName: 'X (Twitter)',
      endpoint: 'https://api.x.com/dsa/enforcement',
      status: 'error',
      verdictsSent: 234,
      successRate: 76.8,
      pendingActions: 18,
      lastSync: 24,
      contentTypes: ['text', 'image'],
      languages: ['en', 'fr', 'es'],
      priority: 'medium',
      dsaCompliant: false,
      lastAudit: '2025-01-08',
      avgResponseTime: 678
    }
  ];

  // Mock data for verdict propagation
  const mockPropagationData = {
    summary: {
      delivered: 2847,
      pending: 156,
      failed: 23,
      processing: 45
    },
    recentPropagations: [
      {
        id: 'prop-1',
        verdictTitle: 'Climate Change Misinformation - Arctic Ice Claims',
        platformCount: 4,
        successCount: 4,
        totalCount: 4,
        status: 'delivered',
        timestamp: '2 hours ago'
      },
      {
        id: 'prop-2',
        verdictTitle: 'Election Integrity - Voting Machine Security',
        platformCount: 6,
        successCount: 5,
        totalCount: 6,
        status: 'processing',
        timestamp: '3 hours ago'
      },
      {
        id: 'prop-3',
        verdictTitle: 'Health Misinformation - Vaccine Efficacy Data',
        platformCount: 3,
        successCount: 1,
        totalCount: 3,
        status: 'failed',
        timestamp: '5 hours ago'
      }
    ],
    platformStatus: [
      {
        id: 'status-1',
        name: 'Meta',
        online: true,
        lastSync: '2 min ago',
        responseTime: 245,
        successRate: 98.5
      },
      {
        id: 'status-2',
        name: 'YouTube',
        online: true,
        lastSync: '5 min ago',
        responseTime: 189,
        successRate: 96.2
      },
      {
        id: 'status-3',
        name: 'TikTok',
        online: false,
        lastSync: '2 hours ago',
        responseTime: 432,
        successRate: 89.3
      }
    ]
  };

  // Mock data for pending policy actions
  const mockPendingActions = [
    {
      id: 'action-1',
      contentTitle: 'Misleading COVID-19 Treatment Claims',
      platform: 'Meta Platforms',
      priority: 'critical',
      flaggedDate: '2025-01-20',
      timeRemaining: '2 hours',
      verdictId: 'VRD-2025-0156'
    },
    {
      id: 'action-2',
      contentTitle: 'Election Fraud Conspiracy Theory',
      platform: 'X (Twitter)',
      priority: 'high',
      flaggedDate: '2025-01-20',
      timeRemaining: '6 hours',
      verdictId: 'VRD-2025-0157'
    },
    {
      id: 'action-3',
      contentTitle: 'Climate Change Denial Content',
      platform: 'YouTube',
      priority: 'medium',
      flaggedDate: '2025-01-19',
      timeRemaining: '1 day',
      verdictId: null
    },
    {
      id: 'action-4',
      contentTitle: 'Vaccine Misinformation Video',
      platform: 'TikTok',
      priority: 'high',
      flaggedDate: '2025-01-19',
      timeRemaining: '8 hours',
      verdictId: 'VRD-2025-0155'
    }
  ];

  // Mock data for compliance tracking
  const mockComplianceData = {
    overallScore: 92.4,
    compliantPlatforms: 6,
    pendingActions: 23,
    platforms: [
      {
        id: 'comp-1',
        name: 'Meta Platforms',
        region: 'EU-Wide',
        complianceScore: 98.5,
        actionsCompleted: 156,
        actionsPending: 3,
        avgResponseTime: 2.4,
        lastUpdate: '2 hours ago',
        recentActions: [
          { id: 'act-1', type: 'Content Labeled', status: 'completed', timestamp: '1 hour ago' },
          { id: 'act-2', type: 'Content Removed', status: 'completed', timestamp: '3 hours ago' },
          { id: 'act-3', type: 'Account Suspended', status: 'pending', timestamp: '5 hours ago' }
        ]
      },
      {
        id: 'comp-2',
        name: 'Google/YouTube',
        region: 'EU-Wide',
        complianceScore: 96.2,
        actionsCompleted: 134,
        actionsPending: 7,
        avgResponseTime: 3.1,
        lastUpdate: '1 hour ago',
        recentActions: [
          { id: 'act-4', type: 'Video Demonetized', status: 'completed', timestamp: '2 hours ago' },
          { id: 'act-5', type: 'Channel Warning', status: 'completed', timestamp: '4 hours ago' },
          { id: 'act-6', type: 'Content Restricted', status: 'pending', timestamp: '6 hours ago' }
        ]
      },
      {
        id: 'comp-3',
        name: 'TikTok',
        region: 'EU-Wide',
        complianceScore: 78.9,
        actionsCompleted: 89,
        actionsPending: 18,
        avgResponseTime: 5.7,
        lastUpdate: '6 hours ago',
        recentActions: [
          { id: 'act-7', type: 'Video Removed', status: 'failed', timestamp: '8 hours ago' },
          { id: 'act-8', type: 'User Notified', status: 'completed', timestamp: '12 hours ago' },
          { id: 'act-9', type: 'Content Flagged', status: 'pending', timestamp: '1 day ago' }
        ]
      }
    ]
  };

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleUpdateSubscriptionStatus = (subscriptionId) => {
    console.log('Testing connection for subscription:', subscriptionId);
    // Implementation for testing VLOP connection
  };

  const handleManageFeed = (subscriptionId) => {
    console.log('Managing feed for subscription:', subscriptionId);
    // Implementation for feed management
  };

  const handleRetryFailedPropagation = (propagationId) => {
    console.log('Retrying failed propagation:', propagationId);
    // Implementation for retrying failed propagations
  };

  const handleViewPropagationDetails = (propagationId) => {
    console.log('Viewing propagation details:', propagationId);
    // Implementation for viewing propagation details
  };

  const handleSubmitPolicyAction = async (actionData) => {
    console.log('Submitting policy action:', actionData);
    // Implementation for submitting policy actions
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true });
      }, 2000);
    });
  };

  const handleViewAuditTrail = () => {
    console.log('Viewing audit trail');
    // Implementation for viewing audit trail
  };

  const handleGenerateComplianceReport = () => {
    console.log('Generating compliance report');
    // Implementation for generating compliance reports
  };

  const handleViewComplianceDetails = (platformId, actionId) => {
    console.log('Viewing compliance details:', platformId, actionId);
    // Implementation for viewing compliance details
  };

  const tabs = [
    { id: 'subscriptions', label: 'VLOP Subscriptions', icon: 'Link' },
    { id: 'propagation', label: 'Verdict Propagation', icon: 'Send' },
    { id: 'policy', label: 'Policy Actions', icon: 'Shield' },
    { id: 'compliance', label: 'Compliance Tracker', icon: 'CheckCircle' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="flex items-center space-x-3">
            <Icon name="Loader" size={24} className="animate-spin text-primary" />
            <span className="text-lg text-foreground">Loading Platform Bridge Interface...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Platform Bridge Interface</h1>
                <p className="text-muted-foreground mt-2">
                  Manage VLOP compliance and verdict distribution across European platforms
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-lg">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-success">DSA Compliant</span>
                </div>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleGenerateComplianceReport}
                >
                  Export Report
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-1 mb-6 bg-muted/30 p-1 rounded-lg">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-soft'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'subscriptions' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {mockSubscriptions?.map((subscription) => (
                    <VLOPSubscriptionCard
                      key={subscription?.id}
                      subscription={subscription}
                      onUpdateStatus={handleUpdateSubscriptionStatus}
                      onManageFeed={handleManageFeed}
                    />
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'propagation' && (
              <VerdictPropagationDashboard
                propagationData={mockPropagationData}
                onRetryFailed={handleRetryFailedPropagation}
                onViewDetails={handleViewPropagationDetails}
              />
            )}

            {activeTab === 'policy' && (
              <PolicyActionPanel
                pendingActions={mockPendingActions}
                onSubmitAction={handleSubmitPolicyAction}
                onViewAuditTrail={handleViewAuditTrail}
              />
            )}

            {activeTab === 'compliance' && (
              <ComplianceTracker
                complianceData={mockComplianceData}
                onGenerateReport={handleGenerateComplianceReport}
                onViewDetails={handleViewComplianceDetails}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PlatformBridgeInterface;